import React, { Fragment, memo } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { useSelector } from 'react-redux';
import { Flex, Box, Text } from 'roo-ui/components';
import Decimal from 'decimal.js';
import Currency from 'components/Currency';
import NumberOfNights from 'components/NumberOfNights';
import PriceBeforeDiscount from 'components/PriceBeforeDiscount';
import OfferPointsPaySlider from './OfferPointsPaySlider';
import OfferCheckoutLink from './OfferCheckoutLink';
import OfferPayableAtProperty from './OfferPayableAtProperty';
import usePointsConverters from 'hooks/points/usePointsConverters';
import { MIN_POINTS_AMOUNT } from 'config';
import { getQueryCheckIn, getQueryCheckOut } from 'store/search/searchSelectors/querySelectors';
import { getPropertyId, getCountry } from 'store/property/propertySelectors';
import { getIsPointsPay } from 'store/ui/uiSelectors';
import CampaignPriceMessage from 'components/CampaignPriceMessage';

const canPayWithPoints = ({ payableAtBooking, convertCashToPoints }) => {
  const payableAtBookingInPoints =
    payableAtBooking.total.currency === 'PTS'
      ? new Decimal(payableAtBooking.total.amount)
      : convertCashToPoints({ cash: payableAtBooking.total.amount });

  return payableAtBookingInPoints.greaterThanOrEqualTo(MIN_POINTS_AMOUNT);
};

// pull out of line to reduce re-renders of OfferPriceBox
// when check in or checkout query is changed
const ConnectedNumberOfNights = () => {
  const checkIn = useSelector(getQueryCheckIn);
  const checkOut = useSelector(getQueryCheckOut);

  return <NumberOfNights checkIn={checkIn} checkOut={checkOut} />;
};

const OfferPriceBox = memo(({ charges, offerType, offerName, offerId, roomTypeId, roomName, onCashPaymentAmountChange }) => {
  const propertyId = useSelector(getPropertyId);
  const isPointsPay = useSelector(getIsPointsPay);
  const country = useSelector(getCountry);

  const { convertCashToPoints } = usePointsConverters();
  const { totalBeforeDiscount, total, payableAtProperty, payableAtBooking, totalCash } = charges;
  const currency = get(charges, 'total.currency');
  const isCurrencyCash = currency !== 'PTS';
  const isClassic = offerType === 'classic';
  const hasDiscount = totalBeforeDiscount && totalBeforeDiscount.amount !== total.amount;
  const isShowingPointsPaySlider = isPointsPay && !isClassic && canPayWithPoints({ isClassic, payableAtBooking, convertCashToPoints });
  const isPayableAtPropertyInclusive = payableAtBooking.total.currency !== 'PTS';

  return (
    <Box width="100%" data-testid="offer-price-box">
      {isShowingPointsPaySlider && (
        <OfferPointsPaySlider
          offerName={offerName}
          totalCash={totalCash || total} //totalCash is a temporary attribute passed by Ava in points mode due to the total in points not being the full total (payableAtBooking + payableAtProperty)
          payableAtProperty={payableAtProperty.total}
          offerId={offerId}
          roomTypeId={roomTypeId}
          propertyId={propertyId}
          onCashPaymentAmountChange={onCashPaymentAmountChange}
          roomName={roomName}
          label={
            <Box as={Text} textAlign="left" width="100%">
              Total for <ConnectedNumberOfNights />
            </Box>
          }
        />
      )}
      {!isShowingPointsPaySlider && (
        <Fragment>
          <Flex flexDirection="row" justifyContent="flex-start" alignItems="baseline" flexWrap="wrap">
            {hasDiscount && (
              <Flex pr={2} data-testid="price-before-discount-box" width="100%">
                <PriceBeforeDiscount
                  size="base"
                  roundToCeiling
                  total={totalBeforeDiscount}
                  hideCurrency={isCurrencyCash}
                  lineHeight="0.6"
                />
              </Flex>
            )}
            <Flex alignItems="center" justifyContent={['flex-start', 'flex-end']}>
              <Currency
                amount={total.amount}
                currency={total.currency}
                roundToCeiling
                fontSize={32}
                hideCurrency={isCurrencyCash}
                fontWeight="normal"
                color="greys.charcoal"
                data-testid="total-to-pay"
              />
            </Flex>
            <Box color="greys.charcoal" pl={2} top={1}>
              {!isShowingPointsPaySlider && isCurrencyCash && <Text> {currency}</Text>}
              <Text> / </Text>
              <ConnectedNumberOfNights />
            </Box>
          </Flex>
          <CampaignPriceMessage currency={total.currency} offerType={offerType} country={country} />
          <OfferPayableAtProperty payableAtProperty={payableAtProperty.total} isIncludedInTotal={isPayableAtPropertyInclusive} />
          <OfferCheckoutLink propertyId={propertyId} roomTypeId={roomTypeId} offerId={offerId} offerName={offerName} roomName={roomName} />
        </Fragment>
      )}
    </Box>
  );
});

OfferPriceBox.propTypes = {
  charges: PropTypes.object.isRequired,
  offerType: PropTypes.string.isRequired,
  roomTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  offerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  offerName: PropTypes.string.isRequired,
  roomName: PropTypes.string.isRequired,
  onCashPaymentAmountChange: PropTypes.func.isRequired,
};

OfferPriceBox.displayName = 'OfferPriceBox';

export default OfferPriceBox;
